/**
 * Flow Router
 * 
 * Client-side routing using History API
 * Supports SPA-style navigation without full page reloads
 */

class FlowRouter {
  constructor(flowConfig) {
    this.config = flowConfig;
    this.urlPrefix = flowConfig.urlPrefix;
    this.routes = this.buildRoutes();
    this.currentRoute = null;
    this.listeners = [];
  }

  /**
   * Build route map from configuration
   * @returns {Object} Route map
   */
  buildRoutes() {
    return {
      // Quiz steps
      [`${this.urlPrefix}/quiz/name`]: 'name',
      [`${this.urlPrefix}/quiz/spayed`]: 'spayed',
      [`${this.urlPrefix}/quiz/age`]: 'age',
      [`${this.urlPrefix}/quiz/breed`]: 'breed',
      [`${this.urlPrefix}/quiz/weight`]: 'weight',
      [`${this.urlPrefix}/quiz/composition`]: 'composition',
      [`${this.urlPrefix}/quiz/activity`]: 'activity',
      [`${this.urlPrefix}/quiz/allergies`]: 'allergies',
      [`${this.urlPrefix}/quiz/snacks`]: 'snacks',
      [`${this.urlPrefix}/quiz/email`]: 'email',
      [`${this.urlPrefix}/quiz/calculation`]: 'calculation',
      
      // Plan steps
      [`${this.urlPrefix}/plan/flavors`]: 'flavors',
      [`${this.urlPrefix}/plan/overview`]: 'plan',
      [`${this.urlPrefix}/plan/bulk`]: 'bulk',
      
      // Profile
      [`${this.urlPrefix}/profile`]: 'profile'
    };
  }

  /**
   * Initialize router
   */
  init() {
    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', (e) => {
      this.handleRouteChange();
    });

    // Handle initial route
    this.handleRouteChange();
  }

  /**
   * Extract locale prefix from pathname (e.g. /en, /en-eu from /en-eu/pages/wizard-1/)
   * Supports 2-letter codes (/en, /nl) and language-region (/en-eu, /en-us, /pt-br)
   * @returns {string} Locale prefix including leading slash, or empty string
   */
  getLocalePrefix() {
    const pathname = window.location.pathname;
    const match = pathname.match(/^(\/[a-z]{2}(-[a-z]{2})?)(?=\/|$)/);
    return match ? match[1] : '';
  }

  /**
   * Get current path from URL
   * Handles query parameter ?__path= for deep linking via Shopify redirects
   * Preserves locale prefix (e.g. /en) when present
   * @returns {string} Current path (without locale for route matching)
   */
  getCurrentPath() {
    // Check for __path query parameter first (from Shopify redirects)
    const urlParams = new URLSearchParams(window.location.search);
    const pathParam = urlParams.get('__path');
    
    if (pathParam) {
      // Restore clean URL by removing query parameter, preserve locale if present
      const localePrefix = this.getLocalePrefix();
      const newUrl = window.location.origin + localePrefix + pathParam + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      return pathParam;
    }

    let path = window.location.pathname;
    const localePrefix = this.getLocalePrefix();
    if (localePrefix) {
      path = path.slice(localePrefix.length) || '/';
    }

    // Normalize Shopify page URLs to SPA prefix
    // Example: /pages/wizard-1 -> /wizard-1
    //          /pages/wizard-1/quiz/name -> /wizard-1/quiz/name
    //          /pages/wizard-one -> /wizard-1 (common page handle variant)
    if (path.startsWith('/pages/wizard-one')) {
      path = path.replace('/pages/wizard-one', '/pages/wizard-1');
    }
    if (path.startsWith('/pages' + this.urlPrefix)) {
      path = path.replace('/pages' + this.urlPrefix, this.urlPrefix);
    } else if (path === `/pages${this.urlPrefix}`) {
      path = this.urlPrefix;
    }

    return path;
  }

  /**
   * Check if path is a root URL (no specific step in path)
   * Handles /wizard-1, /pages/wizard-1, /pages/wizard-one, etc.
   */
  isRootUrl(path, step) {
    if (!step) return true;
    if (path === `${this.urlPrefix}/` || path === this.urlPrefix) return true;
    if (path === `/pages/wizard-1/` || path === `/pages/wizard-1`) return true;
    if (path === '/pages/wizard-one' || path === '/pages/wizard-one/') return true;
    if (!path.includes('/quiz/') && !path.includes('/plan/') && path !== `${this.urlPrefix}/profile`) return true;
    return false;
  }

  /**
   * Get step identifier from route
   * @param {string} path - URL path
   * @returns {string|null} Step identifier or null
   */
  getStepFromPath(path) {
    // Check exact matches first
    if (this.routes[path]) {
      return this.routes[path];
    }

    // Check parameterized routes (no longer needed for profile, but keep for future routes)
    for (const [route, step] of Object.entries(this.routes)) {
      if (route.includes(':')) {
        const routePattern = route.replace(/:[^/]+/g, '([^/]+)');
        const regex = new RegExp(`^${routePattern}$`);
        if (regex.test(path)) {
          return step;
        }
      }
    }

    return null;
  }

  /**
   * Get route parameters from path and query string
   * For profile step, extracts 'pet' from query string (?pet=gid://...)
   * @param {string} path - URL path
   * @returns {Object} Route parameters
   */
  getRouteParams(path) {
    const params = {};
    
    // Check parameterized routes (for future routes that might use path params)
    for (const [route, step] of Object.entries(this.routes)) {
      if (route.includes(':')) {
        const routePattern = route.replace(/:[^/]+/g, '([^/]+)');
        const regex = new RegExp(`^${routePattern}$`);
        const match = path.match(regex);
        
        if (match) {
          const paramNames = route.match(/:[^/]+/g) || [];
          paramNames.forEach((paramName, index) => {
            const key = paramName.replace(':', '');
            // Browser automatically decodes window.location.pathname, so match is already decoded
            params[key] = match[index + 1];
          });
        }
      }
    }

    // Extract query parameters (for profile step, get 'pet' from ?pet=gid://...)
    const urlParams = new URLSearchParams(window.location.search);
    const petParam = urlParams.get('pet');
    if (petParam) {
      params.pet = decodeURIComponent(petParam);
    }

    return params;
  }

  /**
   * Navigate to a route
   * @param {string} step - Step identifier
   * @param {Object} params - Route parameters (optional, for path params)
   * @param {boolean} replace - Replace current history entry (default: false)
   * @param {Object} queryParams - Query parameters (optional, for ?key=value)
   */
  navigate(step, params = {}, replace = false, queryParams = {}) {
    let path = null;

    // Find route for step
    for (const [route, routeStep] of Object.entries(this.routes)) {
      if (routeStep === step) {
        // Replace parameters in route (for path params like :id)
        path = route;
        // Separate path params from query params
        const pathParams = {};
        const queryParamsFromParams = {};
        
        Object.keys(params).forEach(key => {
          if (route.includes(`:${key}`)) {
            // This is a path parameter
            pathParams[key] = params[key];
          } else if (step === 'profile' && key === 'pet') {
            // This is a query parameter for profile step
            queryParamsFromParams[key] = params[key];
          }
        });
        
        // Replace path parameters in route
        Object.keys(pathParams).forEach(key => {
          const encodedValue = encodeURIComponent(pathParams[key]);
          path = path.replace(`:${key}`, encodedValue);
        });
        
        // Add query params from params object
        Object.assign(queryParams, queryParamsFromParams);
        break;
      }
    }

    if (!path) {
      console.error(`Route not found for step: ${step}`);
      return;
    }

    // Handle query parameters (especially for profile step with ?pet=gid://...)
    // If step is 'profile' and params has 'pet' key, add it as query param
    if (step === 'profile' && params.pet) {
      queryParams.pet = params.pet;
    }

    // Build query string if queryParams exist
    if (Object.keys(queryParams).length > 0) {
      const queryString = Object.keys(queryParams)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      path = `${path}?${queryString}`;
    }

    // Always use clean URLs (/wizard-1) so browser back from checkout works (Shopify redirects exist for /wizard-1/*)
    // Apply locale prefix when present (e.g. /en/wizard-1/plan/bulk)
    const localePrefix = this.getLocalePrefix();
    if (localePrefix) {
      path = localePrefix + path;
    }

    // Update URL
    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }

    // Trigger route change
    this.handleRouteChange();
  }

  /**
   * Navigate to next step
   * @param {string} currentStep - Current step identifier
   */
  navigateNext(currentStep) {
    const stepOrder = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'flavors', 'plan', 'bulk'
    ];

    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[currentIndex + 1];
      this.navigate(nextStep);
    }
  }

  /**
   * Navigate to previous step
   * @param {string} currentStep - Current step identifier
   */
  navigatePrevious(currentStep) {
    // Special case: When on flavors step, go directly to email (skipping calculation)
    // This is because calculation step auto-redirects back to flavors, making it
    // confusing for users to go back from flavors
    if (currentStep === 'flavors') {
      this.navigate('email');
      return;
    }

    const stepOrder = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'flavors', 'plan', 'bulk'
    ];

    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      const previousStep = stepOrder[currentIndex - 1];
      this.navigate(previousStep);
    }
  }

  /**
   * Handle route change
   */
  handleRouteChange() {
    const path = this.getCurrentPath();
    let step = this.getStepFromPath(path);
    let params = this.getRouteParams(path);

    // Check if this is a root/redirect URL (not a specific step URL)
    // Root URLs are: /wizard-1/, /pages/wizard-1/, or URLs without a step path
    // Note: path is already normalized, so /pages/wizard-1 becomes /wizard-1
    const isRootUrl = !step || 
                     path === `${this.urlPrefix}/` || 
                     path === this.urlPrefix ||
                     path === `/pages/wizard-1/` ||
                     path === `/pages/wizard-1` ||
                     (!path.includes('/quiz/') && !path.includes('/plan/') && path !== `${this.urlPrefix}/profile`);

    // If this is a root URL (no step in path), check for saved state first
    // BEFORE defaulting to 'name' step
    if (isRootUrl && window.wizard1Flow && window.wizard1Flow.state && !window.wizard1Flow.initialized) {
      const petData = window.wizard1Flow.state.getPetData();
      const hasLoadedData = petData.name || 
                           petData.gender ||
                           petData.ageGroup ||
                           petData.breed ||
                           petData.weight !== null ||
                           window.wizard1Flow.state.customerId;
      
      // If we have loaded data, auto-redirect to furthest valid step
      if (hasLoadedData) {
        // Get the furthest valid step from saved state
        const furthestValidStep = window.wizard1Flow.state.getFurthestValidStep();
        
        // Use furthest valid step
        if (furthestValidStep) {
          step = furthestValidStep;
          const stepToPath = this.getPathForStep(furthestValidStep);
          if (stepToPath) {
            const localePrefix = this.getLocalePrefix();
            const fullPath = localePrefix ? localePrefix + stepToPath : stepToPath;
            window.history.replaceState({}, '', fullPath);
          }
          // Update state to match the step we're navigating to
          window.wizard1Flow.state.setCurrentStep(step);
        } else {
          // No valid step found, default to 'name'
          step = 'name';
          window.wizard1Flow.state.setCurrentStep(step);
        }
      } else {
        // No loaded data, default to 'name' step
        step = 'name';
        window.wizard1Flow.state.setCurrentStep(step);
      }
    } 
    // If no step found and it's not a root URL (shouldn't happen, but handle it)
    else if (!step) {
      step = 'name';
      if (window.wizard1Flow && window.wizard1Flow.state) {
        window.wizard1Flow.state.setCurrentStep(step);
      }
    }
    // If state is already loaded and this is a specific step URL (not root)
    // Update state to match URL step
    else if (window.wizard1Flow && window.wizard1Flow.state && !window.wizard1Flow.initialized) {
      window.wizard1Flow.state.setCurrentStep(step);
    }
    // Flow is initialized, update state to match URL (normal navigation)
    else if (window.wizard1Flow && window.wizard1Flow.state) {
      window.wizard1Flow.state.setCurrentStep(step);
    }

    this.currentRoute = { step, path, params };
    this.notifyListeners(step, params);
  }

  /**
   * Get URL path for a step
   * @param {string} step - Step identifier
   * @returns {string|null} URL path or null if not found
   */
  getPathForStep(step) {
    for (const [route, routeStep] of Object.entries(this.routes)) {
      if (routeStep === step) {
        return route;
      }
    }
    return null;
  }

  /**
   * Add route change listener
   * @param {Function} callback - Callback function (step, params) => {}
   */
  onRouteChange(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove route change listener
   * @param {Function} callback - Callback function to remove
   */
  offRouteChange(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Notify all listeners of route change
   * @param {string} step - Step identifier
   * @param {Object} params - Route parameters
   */
  notifyListeners(step, params) {
    this.listeners.forEach(listener => {
      try {
        listener(step, params);
      } catch (error) {
        console.error('Error in route change listener:', error);
      }
    });
  }

  /**
   * Get current route
   * @returns {Object} Current route object
   */
  getCurrentRoute() {
    return this.currentRoute;
  }
}

// Export for global access
window.FlowRouter = FlowRouter;

